import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, from, defer, throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { catchError, concatMap, map, switchMap, timeout } from 'rxjs/operators';
import { Message } from '../models/message.model';
import { ChatAction } from '../models/chat-action.model';
import { FruHttpService } from '../../../../services/fru-http.service';
import { updateRateFinderSentryContext } from '../../../../services/rate-finder-sentry';
import { MC_PAYMENT_PENDING_STORAGE_KEY, mcPaymentPendingBinding, mcPaymentPendingRestored, mcPaymentPendingSaved } from '../models/mortgage-calc-payment-test';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  loanPurposeShown = false;
  /** The visitor answered the loan-type question on this page. Unlike loanPurposeShown (set when the component is built,
   * and every step is built up front), this is only true after a real answer, so a property-type step rebuilt after that
   * tap can tell itself apart from a landing where property type is the first question. */
  loanPurposeAnsweredHere = false;
  /** FRU-23392: the entry already named the loan type (a keyword the landing understood), so the chat started past the
   * loan-type question without ever showing it. The loan-type component was still built (and set loanPurposeShown), so
   * without this the first question the visitor actually sees had no opening, rate or APR. */
  loanPurposeSkippedOnEntry = false;
  /** FRU-23392: the step the chat actually opened on after a seed or a saved resume. Only that step may take the opening and
   * its rate: every step is built up front, and a hidden step's rate request would take the scope from the visible one. */
  entryStepKey = '';
  /** FRU-23824: the step this chat opens on before a saved resume jumps past it (the cash question on the short home equity
   *  form). A reload resumes at the saved step, which takes the opening; coming back here hands it back (HomeValueOpening). */
  naturalEntryStepKey = '';
  /** FRU-23403: a real mortgage balance is known in the short form (the property lookup's), so the over-80% offer stays out. */
  heShortBalanceKnown = false;
  suppressLoanPurposeIntro = false;
  // 2026-09-23 (Ed approved "option 2"): the first-screen rate headline on generic and calculator
  // landings, the same big "5.5% 5.7 APR*" the original form leads with. Set by the loan-type step
  // only when a live fixed pair paints; null otherwise, and the bubble then keeps its own rate.
  // inline: one line, title then the pair (the short home equity form's cash screen, FRU-23403).
  landingRateHero: { title: string; lead: string; rate: string; apr: string; subtitle: string; inline?: boolean; pending?: boolean; pendingLead?: string; calc?: boolean; aprOnly?: boolean } | null = null;
  // FRU-23221: this visitor's first-screen variant; undefined until the loan-type step decides, null when not enrolled.
  firstScreenVariant: 'original' | 'NoChatFraming' | null | undefined = undefined;
  // Ed 2026-09-24: desktop reads like Claude and ChatGPT -- the headline above the conversation, Hope just asks the
  // question, the message box docked at the bottom and narrower than the column. Decided once, when the chat starts.
  deskLayout = false;
  // FRU-23316: Hope Direct wears the calculator skin (calculator landings only; decided once in ChatComponent).
  calcSkin = false;
  /** FRU-23637: the first screen is the mortgage calculator (models/mortgage-calc), decided once with calcSkin. */
  mortgageCalc = false;
  /** FRU-23637: the answer the rebuilt conversation shows for the loan type step when the calculator answered it. */
  loanTypeEchoOverride = '';
  // FRU-23386: this visitor's version of the home-equity first screen ('amount' asks how much cash first), decided once in
  // ChatComponent; null when not in the test. amountFirstLogged: the version was recorded (once, when the screen showed).
  amountFirstArm: 'amount' | 'control' | null = null;
  amountFirstLogged = false;
  amountFirstEnrollmentMarker = '';
  // FRU-23389: which calculator-landing version this chat gets (models/calc-payments), decided once in ChatComponent; null
  // when not in the test. calcPaymentsLogged: the version was recorded (once, when the first screen showed).
  calcPaymentsArm: 'payments' | 'control' | null = null;
  calcPaymentsLogged = false;
  calcPaymentsEnrollmentMarker = '';
  calcPaymentsExperimentEligible = false;
  // FRU-23777: the mortgage calculator payment test (models/mortgage-calc-payment-test): 'held' shows "Calculated" until the
  // Match page. Decided once in ChatComponent; null when not in the test. mcPaymentLogged: recorded once, when the calculator's
  // first screen showed. mcPaymentPending: the held version's priced snapshot, written for the Match page only once a submit
  // is accepted (stamped with that lead).
  mcPaymentArm: 'live' | 'held' | null = null;
  mcPaymentLogged = false;
  mcPaymentEnrollmentMarker = '';
  mcPaymentExperimentEligible = false;
  // FRU-23794: the contact-first buttons test. contactFirstArm: this chat's version (null when not in the test).
  // contactFirstChannel: the button the visitor tapped. contactFirstPhase: true while the name and contact are asked up front
  // (until the loan type is answered), so the phone step knows it isn't the final one yet.
  contactFirstArm: 'buttons' | 'control' | null = null;
  contactFirstChannel: 'text' | 'email' | null = null;
  // FRU-23815: the visitor ticked (or unticked) "100% digital" on the up-front phone step this visit; null when untouched.
  prefersDigitalChosen: boolean | null = null;
  // FRU-24551: the ZIP Cloudflare places this visitor in (public_api/ip_state) and that reply's state. A guess from the IP:
  // the ZIP step may start with it for the visitor to confirm or change, and never submits it unseen.
  visitorZip: string | null = null;
  visitorZipState: string | null = null;
  // FRU-23815: the first screen the buttons were on: Hope's loan type screen, or the short home equity flow's cash question.
  contactFirstEntryKey: 'loanPurpose' | 'maximumEquity' | 'propertyType' = 'loanPurpose';
  /** FRU-23796: the server's token for this visitor's phone-first row (Text Me Rates); sent with later answers and the submit. */
  hopePhoneFirstToken: string | null = null;
  contactFirstPhase = false;
  contactFirstLogged = false;
  contactFirstExperimentEligible = false;
  contactFirstEnrollmentMarker = '';
  // Kept in the tab as well (models/mortgage-calc-payment-test, MC_PAYMENT_PENDING_STORAGE_KEY) in the held version, so a reload
  // still unlocks the payment; every write here updates or clears that copy.
  private mcPaymentPendingValue: any = null;
  get mcPaymentPending(): any { return this.mcPaymentPendingValue; }
  set mcPaymentPending(rec: any) {
    this.mcPaymentPendingValue = rec || null;
    try {
      const saved = mcPaymentPendingSaved(this.mcPaymentPendingValue, mcPaymentPendingBinding(this.mcPaymentArm, this.mcPaymentEnrollmentMarker), Date.now());
      if (saved) { sessionStorage.setItem(MC_PAYMENT_PENDING_STORAGE_KEY, saved); }
      else { sessionStorage.removeItem(MC_PAYMENT_PENDING_STORAGE_KEY); }
    } catch (e) { /* storage blocked: the in-memory copy still serves this page */ }
  }
  /** At chat start, once the version and marker are decided: the held snapshot this tab kept for this enrollment, or none. */
  restoreMcPaymentPending(now: number = Date.now()): void {
    let rec: any = null;
    try {
      rec = mcPaymentPendingRestored(sessionStorage.getItem(MC_PAYMENT_PENDING_STORAGE_KEY),
        mcPaymentPendingBinding(this.mcPaymentArm, this.mcPaymentEnrollmentMarker), now);
    } catch (e) { rec = null; }
    this.mcPaymentPending = rec;   // re-saves a valid copy, clears anything else
  }
  // FRU-23398: the neutral loan-type hierarchy test (models/loantype-hierarchy). Arm decided once per chat; counted once the
  // tiles were on screen for the reading beat or answered.
  loanTypeHierarchyArm: 'hierarchy' | 'control' | null = null;
  loanTypeHierarchyLogged = false;
  loanTypeHierarchyEnrollmentMarker = '';
  loanTypeHierarchyExperimentEligible = false;
  loanTypeHierarchyDecided = false;
  // FRU-23403: the short home equity form (models/he-short-form), set per chat: switched on (or he_short=1|0 on a test visit),
  // and whether its first fill-in was recorded.
  heShortOn = false;
  /** FRU-24042: a Google Search home equity visitor opens on Hope's loan type screen (models/search-loantype-first). */
  searchLoanTypeFirst = false;
  heShortForcedVisit = false;
  heShortLogged = false;
  // FRU-23418: which home value screen the short form shows this visitor (tiles or the slider), and whether it was counted.
  heValueArm: 'tiles' | 'slider' | null = null;
  heValueForced = false;
  heValueLogged = false;
  heValueEnrollmentMarker = '';
  // FRU-23192: the short flow's email-value test (models/email-value). emailValueCohortNow is the chat's live check that the
  // visitor belongs to the test right now (the short home equity flow applies, and the chat is not in Spanish, which asks
  // its own question), set by the chat so the email step and the lead read the same answer.
  emailValueArm: 'value' | 'current' | null = null;
  emailValueForced = false;
  emailValueLogged = false;
  emailValueEnrollmentMarker = '';
  emailValueCohortNow: (() => boolean) | null = null;
  loanTypeHierarchyVisitorId = '';
  // FRU-23389: the reviewed 30-year pair per payment purpose (refinance, cash_out, purchase), fetched without taking over
  // the page's rate scope once the visitor's loan type is known; the payment is priced on it.
  calcPaymentsRates: { [purpose: string]: any } = {};
  // Desktop: the opening's headline lines once they are lifted above the conversation ('' when none). Shared so a later
  // step that would re-open with the same product and rate line asks only its question instead.
  deskHeadlineHtml = '';
  /** The step key that wrote deskHeadlineHtml, so only that step's Spanish switch clears it. */
  deskHeadlineOwner = '';
  // The advertised-rate scope each desktop headline's numbers came from (rateBucketKey). A headline shows its pair
  // ONLY while that scope is still the active one, so a later request that switches the scope -- the property-type
  // step after a Purchase tap -- can never leave the previous scope's pair on screen against the new disclosure.
  // undefined = not bound (legacy rates mode records no scope).
  landingRateHeroScopeKey: string | undefined = undefined;
  /** FRU-23403: the cash screen heatmap was decided (captured or not) for this page, and whether it was captured. */
  cashHeatmapDecided = false;
  cashHeatmapCaptured = false;
  /** FRU-23403: a returning welcome card was offered on this page; and the returning decision now (set by ChatComponent). */
  returningCardShown = false;
  returningStateNow: (() => 'card' | 'pending' | 'none') | null = null;
  deskHeadlineScopeKey: string | undefined = undefined;

  /** The active advertised-rate scope key, or undefined in legacy rates mode (no scoped disclosures). */
  activeRateScopeKey(): string | null | undefined {
    try {
      const w: any = typeof window !== 'undefined' ? window : null;
      if (!w || !('fruActiveEffectiveAdvertisedRateKey' in w)) { return undefined; }
      return w.fruActiveEffectiveAdvertisedRateKey;
    } catch (e) { return undefined; }
  }

  /** The key to bind a painted pair to: its own bucket, or undefined when no scope is tracked. */
  rateScopeKeyFor(rate: any): string | undefined {
    try {
      if (this.activeRateScopeKey() === undefined) { return undefined; }
      return rate && rate.purpose && rate.product ? `${String(rate.purpose)}.${String(rate.product)}` : undefined;
    } catch (e) { return undefined; }
  }

  /** True while the scope a desktop headline pair was bound to is still the active one. */
  rateScopeStillActive(key: string | undefined): boolean {
    if (key === undefined) { return true; }
    return this.activeRateScopeKey() === key;
  }
  // Ed 7/24: the resolved product presentation (program/keyword-aware), shared so landing steps
  // can weave the SAME clause into a combined intro+question bubble.
  hopePresentation: any = null;
  private currentTypeSource = new BehaviorSubject<string>('airatefinder');
  currentType = this.currentTypeSource.asObservable();
  private chat_threadId: string;
  private threadIdInit: Promise<string> | null = null;
  private threadSeeded = false;  // codex #3: a resumed threadid we have not yet proven valid
  private resolveThreadIdInit: ((id: string) => void) | null = null;
  private actionQueue$ = new Subject<ChatAction>();

  // FRU-21526 (Ed 7/10): a step's "Reply to Hope" / "Back to options" toggle asks the chat to open or
  // close the composer. Subjects (not @Output) cross the dynamic step-component layering.
  private openHopeSource = new Subject<void>();
  openHope$ = this.openHopeSource.asObservable();
  requestOpenHope(): void { this.openHopeSource.next(); }
  // FastRate Property Lookup: a resolved address pick -> chat component back-fills the form.
  private fastRateSource = new Subject<any>();
  fastRate$ = this.fastRateSource.asObservable();
  requestFastRate(data: any): void { this.fastRateSource.next(data); }
  // FRU-23386: a choice on the home-equity amount screen -> chat component commits it (see commitAmountFirst).
  private amountFirstSource = new Subject<any>();
  amountFirst$ = this.amountFirstSource.asObservable();
  requestAmountFirst(choice: any): void { this.amountFirstSource.next(choice); }
  // Ed 7/10 (AG #3): true while the ⚡ FastRate panel is open, so the chat suppresses the
  // Ask-Hope composer -- the panel owns its own inputs; typing to Hope there dead-ends on "tap an option".
  fastRateActive = false;
  // FRU-21526 (Ed 7/16): true only once a FastRate/REAPI lookup actually delivered a usable estimated
  // value. propertyContextLift (email/phone lead-in) recites value/equity ONLY when this is set — the
  // form controls may hold untouched slider defaults, which must never be quoted back as the consumer\u2019s
  // own numbers (live incident: consumer challenged "$20,000 in equity" instead of answering the email step).
  fastRatePropertyApplied = false;
  // FRU-21733 (AG 7/15, Android P0): stamped when a typed AMOUNT advances via Enter/Go, so the
  // trailing window keyup Enter is swallowed and does NOT auto-submit the NEXT step's default option.
  lastEnterAdvanceAt = 0;
  // Passthrough 7/10: once the consumer dismisses or completes FastRate, never force-reopen it
  // (?fastrate=1 re-runs initStep on goBack); and the global Enter KEYUP handler must stand down
  // briefly after any FastRate resolve/cancel (the keydown's stopPropagation cannot stop the keyup).
  fastRateDismissed = false;
  fastRateGuardUntil = 0;
  // Ed 7/10 (FastRate): back-fill hints the balance step reads to pre-fill its default + show the lender.
  fastRatePrefill: { firstMortgageBalance: number | null; cashOut?: number | null; currentLender: string } | null = null;
  private closeHopeSource = new Subject<void>();
  closeHope$ = this.closeHopeSource.asObservable();
  requestCloseHope(): void { this.closeHopeSource.next(); }
  // FRU-21526 (Ed 7/10): in-bubble "back" -> chat goes one question back. canGoBackNow gates the
  // affordance (chat sets it per step) so it never shows on the first real question.
  canGoBackNow = false;
  private backSource = new Subject<void>();
  back$ = this.backSource.asObservable();
  requestBack(): void { this.backSource.next(); }
  // FRU-23413: a step that grew under the docked box (the ZIP step's street field opening) asks the chat to seat its
  // controls above the box again. The chat does the scrolling, so there is still one scroll owner.
  private stepGrewSource = new Subject<string>();
  stepGrew$ = this.stepGrewSource.asObservable();
  requestStepSeat(stepKey: string): void { this.stepGrewSource.next(stepKey); }
  // FRU-23778: a step whose content changed under a reply (the calculator taking back its added line on Back) asks the chat to
  // place it as it does after a reply: the newest reply's question in view, the step's controls above the message box.
  private replyRevealSource = new Subject<string>();
  replyReveal$ = this.replyRevealSource.asObservable();
  requestReplyReveal(stepKey: string): void { this.replyRevealSource.next(stepKey); }

  // Same-step controls occasionally grow after a choice (for example maximum-equity Yes/No ->
  // amount). The dynamic child describes the geometry work, but ChatComponent owns when it may run:
  // every delayed pass is tied to the exact current wrapper/instance and is cancelled on rebuild,
  // Back, advance or Hope teardown. This keeps step components from creating independent scroll
  // timers that can move a replacement conversation.
  private ownedScrollSource = new Subject<{ instance: any; delays: number[]; work: () => void }>();
  ownedScroll$ = this.ownedScrollSource.asObservable();
  requestOwnedScroll(instance: any, work: () => void, delays: number[] = [0]): void {
    if (!instance || typeof work !== 'function') { return; }
    const safeDelays = (Array.isArray(delays) ? delays : [0])
      .map((delay) => Math.max(0, Number(delay) || 0))
      .slice(0, 8);
    this.ownedScrollSource.next({ instance, work, delays: safeDelays.length ? safeDelays : [0] });
  }

  private messagesSubject = new BehaviorSubject<Message[]>([
    new Message(false, 'Welcome to our mortgage chat service! How can we assist you today?'),
    new Message(true, 'I\'m interested in getting a new mortgage.'),
    new Message(
      false,
      'Great! We can help you with that. Are you looking for a fixed-rate or adjustable-rate mortgage?',
    )
  ]);
  private messages$ = this.messagesSubject.asObservable();

  constructor(private fruHttp: FruHttpService,) {
    this.actionQueue$.pipe(
      concatMap(action =>
        this.saveAction(action).pipe(
          catchError((error) => {
            console.error('Failed to persist chat action', error);
            return of(null);
          }),
        )
      )
    ).subscribe();
  }

  changeType(newType: string): void {
    this.currentTypeSource.next(newType);
  }

  getMessages(): Observable<Message[]> {
    return this.messages$;
  }

  sendMessage(message: Message): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  /**
   * Returns the current threadId, or null if this is the first request.
   * If another request is already initializing the threadId, waits for it.
   */
  // FRU-21526 (abx P0-3): thread acquisition as a CANCELLABLE observable. The old Promise-based
  // getThreadId re-entered itself inside `threadIdInit.then(...)`, which minted a NEW owner gate as a
  // SIDE EFFECT even when the awaiting subscription had already been torn down (a 15s timeout, a
  // superseding switchMap, component destroy). That orphan gate never resolved, so every later Hope
  // call waited on it forever. Promoting inside the observable means a torn-down subscription never
  // reaches the gate creation: from(threadIdInit) unsubscribes and the re-drive switchMap never runs.
  private acquireThreadId$(): Observable<{ id: string | null; owner: boolean }> {
    return defer(() => {
      if (this.chat_threadId) { return of({ id: this.chat_threadId, owner: false }); }
      if (this.threadIdInit) {
        // Wait for the in-flight gate. If it resolves null (the owner failed/canceled and cleared the
        // gate), RE-DRIVE acquisition inside THIS observable. Because JS delivers the resolution to each
        // waiter as a sequential microtask, exactly ONE re-driver finds threadIdInit null and becomes the
        // successor owner; the rest find the new gate and wait. Single-successor, and a canceled
        // subscription simply never re-drives (no phantom owner left holding an unresolved gate).
        return from(this.threadIdInit).pipe(
          switchMap((id) => (id ? of({ id, owner: false }) : this.acquireThreadId$()))
        );
      }
      // First caller -> OWNER. The gate is created on subscribe (inside defer), so a subscription torn
      // down before this point never mints a gate.
      this.threadIdInit = new Promise((resolve) => { this.resolveThreadIdInit = resolve; });
      return of({ id: null, owner: true });
    });
  }

  // FRU-21566: seed the threadId from a prior visit so the next request resumes that thread's memory.
  seedThreadId(id: string): void {
    if (!id || this.chat_threadId) { return; }
    this.chat_threadId = id;
    updateRateFinderSentryContext({ thread_id: id });
    this.threadSeeded = true;
    if (this.resolveThreadIdInit) { this.resolveThreadIdInit(id); this.resolveThreadIdInit = null; }
  }

  getCurrentThreadId(): string | null {
    return this.chat_threadId || null;
  }

  // ── Ed/AG 7/20 (abx): ONE shared pin-cancel state for BOTH pin writers ──────────────────────────
  // The phone-step pins (PhoneComponent.initStep) and the settle/phone re-pins (ChatComponent
  // scrollConversationToBottom: 420/820ms + 430/950ms) all consult this. newPinGen() bumps a generation
  // on each step advance; any user GESTURE (pointerdown/keydown/touchmove/wheel) stamps _engagedGen at
  // the current gen, so pinsAllowed() goes false for the rest of that step and no re-pin moves the fold
  // under the finger or above the keyboard. Reflows read the SAME gen, so they stay cancelled too.
  private _pinGen = 0;
  private _engagedGen = -1;
  private _engagementSerial = 0;
  private _pinEngageBound = false;
  // Ed/AG 7/20 (abx P0-1, the "spasm"): while the OWNER (ChatComponent.scrollConversationToBottom)
  // positions the fold after an advance, peripheral scroll writers (step.component append-reveal,
  // adjustMainForm outer scroll, max-equity pin) must stand down -- two animated scrolls per advance,
  // in opposite directions, are the visible jank from the credit step onward. Stamped at advance start
  // (newPinGen) AND whenever the owner runs; a peripheral writer bails while Date.now() < pinOwnerUntil.
  pinOwnerUntil = 0;
  newPinGen(): number { this._pinGen++; this.pinOwnerUntil = Date.now() + 1200; this.bindPinEngage(); return this._pinGen; }
  currentPinGen(): number { this.bindPinEngage(); return this._pinGen; }
  pinsAllowed(gen: number): boolean { return this._engagedGen < gen; }
  // A reply may reveal its same-step answer after the gesture that sent it, but any NEW gesture
  // after the answer paints must cancel that delayed reveal. Pin generation alone cannot
  // distinguish those two moments because both belong to the same step.
  currentEngagementSerial(): number { this.bindPinEngage(); return this._engagementSerial; }
  private bindPinEngage(): void {
    if (this._pinEngageBound) { return; }
    this._pinEngageBound = true;
    try {
      const mark = () => { this._engagedGen = this._pinGen; this._engagementSerial++; };
      const h: any = (typeof document !== 'undefined') ? (document.getElementById('ai-assistant-host') || document) : null;
      // GESTURES only -- NOT the 'scroll' event (our own programmatic scrolls fire it and would self-cancel).
      if (h && h.addEventListener) { ['pointerdown','keydown','touchmove','wheel'].forEach((ev) => h.addEventListener(ev, mark, { passive: true, capture: true })); }
    } catch (e) { /* non-fatal */ }
  }

  private captureThreadId(response: any): void {
    if (!this.chat_threadId && response?.success && response?.threadid) {
      this.chat_threadId = response.threadid;
      updateRateFinderSentryContext({ thread_id: response.threadid });
      if (this.resolveThreadIdInit) {
        this.resolveThreadIdInit(response.threadid);
        this.resolveThreadIdInit = null;
        this.threadIdInit = null;
      }
    }
    // codex #3: a seeded (resumed) thread that answers successfully is proven valid — stop treating it as suspect.
    if (this.chat_threadId && response?.success) { this.threadSeeded = false; }
  }

  // codex #4/#3: on request error, never leave waiters hung, and drop an unproven (stale) resumed thread
  // so the next request mints a fresh one (self-heals a 404 from a thread that no longer exists).
  private onRequestError(isOwner: boolean = true): void {
    // abx 470: ONLY the owning initializer may resolve/reset the shared gate. A waiter's timeout/unsubscribe
    // resolving it woke every waiter into concurrent threadless requests, splitting/minting threads.
    if (isOwner && !this.chat_threadId && this.resolveThreadIdInit) {
      const resolve = this.resolveThreadIdInit;
      this.threadIdInit = null;
      this.resolveThreadIdInit = null;
      resolve(null as any);
    }
    // A stale (unproven) resumed thread that errored self-heals regardless of owner so the next request re-mints.
    if (this.threadSeeded && this.chat_threadId) {
      this.chat_threadId = undefined as any;
      this.threadSeeded = false;
      updateRateFinderSentryContext({ thread_id: undefined });
    }
  }

  // FastRate Property Lookup (Phase-3): thin pass-throughs to the backend REAPI proxy.
  addressAutocomplete(search: string): Observable<any> { return this.fruHttp.addressAutocomplete(search); }
  propertyLookup(id: string): Observable<any> { return this.fruHttp.propertyLookup(id, this.getCurrentThreadId()); }
  returningLookup(vid: string): Observable<any> { return this.fruHttp.returningLookup(vid); }
  returningClaim(vid: string, phone: string): Observable<any> { return this.fruHttp.returningClaim(vid, phone); }
  ensureFastRateHeaders(): void { try { (this.fruHttp as any).ensureFastRateHeaders(); } catch (e) { /* non-fatal */ } }
  // FRU-21526 (Ed 7/10): live-tunable timer read -- window (landing inject) -> sessionStorage -> fetch-once,
  // always falling back to the shipped default so a missing/broken config can never stall Hope.
  uiCfgMs(key: string, fallback: number): number {
    try {
      const w: any = window as any;
      let c = w.fruUiConfig;
      if (!c) {
        try { c = JSON.parse(window.sessionStorage.getItem('fruUiConfig') || 'null'); if (c) { w.fruUiConfig = c; } } catch (e) { /* noop */ }
      }
      if (!c) { try { (this.fruHttp as any).ensureUiConfig(); } catch (e) { /* non-fatal */ } }
      const v = c ? Number(c[key]) : NaN;
      if (isFinite(v) && v > 0 && v <= 120000) { return v; }
    } catch (e) { /* non-fatal */ }
    return fallback;
  }

  // codex (7/14): canonical boolean ui_config read -- accepts bool true/false and the string forms
  // on/off/true/false/1/0 so the API contract and the FE agree (the fastrate_tile lever).
  uiCfgBool(key: string, fallback: boolean): boolean {
    const v = this.uiCfgRaw(key);
    if (v === undefined || v === null || v === '') { return fallback; }
    if (typeof v === 'boolean') { return v; }
    const s = ('' + v).toLowerCase();
    if (s === 'on' || s === 'true' || s === '1' || s === 'yes') { return true; }
    if (s === 'off' || s === 'false' || s === '0' || s === 'no') { return false; }
    return fallback;
  }

  // FRU-21694 (Ed 7/13 PM): RAW ui_config read (objects / booleans -- the product_intro map and its
  // master switch). Same window -> sessionStorage -> fetch-once channel; undefined when not set.
  uiCfgRaw(key: string): any {
    try {
      const w: any = window as any;
      let c = w.fruUiConfig;
      if (!c) { try { c = JSON.parse(window.sessionStorage.getItem('fruUiConfig') || 'null'); if (c) { w.fruUiConfig = c; } } catch (e) { /* noop */ } }
      if (!c) { try { (this.fruHttp as any).ensureUiConfig(); } catch (e) { /* non-fatal */ } }
      return c ? c[key] : undefined;
    } catch (e) { /* non-fatal */ }
    return undefined;
  }

  // FRU-21526 (Ed 7/11): string ui_config read (intro copy/flags) -- same live-tunable channel as uiCfgMs.
  uiCfgStr(key: string, fallback: string): string {
    try {
      const w: any = window as any;
      let c = w.fruUiConfig;
      if (!c) { try { c = JSON.parse(window.sessionStorage.getItem('fruUiConfig') || 'null'); if (c) { w.fruUiConfig = c; } } catch (e) { /* noop */ } }
      if (!c) { try { (this.fruHttp as any).ensureUiConfig(); } catch (e) { /* non-fatal */ } }
      const v = c ? c[key] : undefined;
      // Hardening: a whitespace-only override must NOT win over the caller's hardcoded fallback
      // (in the one-bubble intro design it would blank the question). Require real content.
      if (typeof v === 'string' && v.trim().length) { return v; }
      if (typeof v === 'number' && isFinite(v)) { return String(v); }
    } catch (e) { /* non-fatal */ }
    return fallback;
  }

  // FRU-23351: `httpTimeoutMs`, when given, bounds only the request to Hope, starting AFTER the thread id is
  // acquired, so time spent waiting on another request's thread initialization never eats into it (CodeRabbit).
  // Callers keep an outer bound for the whole call so a stuck acquisition still ends.
  /** FRU-23980: Hope has given a real answer in this chat: a successful reply with words in it once its control tokens are
   *  removed. Errors, timeouts, blank completions and token-only replies never set it. The AI note keys off this. */
  aiAnswered = false;

  private noteAiAnswer(response: any): void {
    try {
      if (!response || !response.success) { return; }
      const words = String(response.message || '').replace(/<<[^>]*>>/g, '').trim();
      if (words) { this.aiAnswered = true; }
    } catch (e) { /* the note is never worth a broken reply */ }
  }

  /** `speculative`: a reply fetched ahead in case the visitor picks a choice (tap-compose prefetch). It is not shown yet, so it
   *  never counts as Hope having answered; the component marks aiAnswered itself if it shows one. */
  saveChatResponse(text: string, step: string, form_data?: any, httpTimeoutMs?: number, speculative = false): Observable<any> {
    let isOwner = false;   // abx 470/474.1: only the owning initializer may release/reset the shared init gate
    return this.acquireThreadId$().pipe(
      switchMap((g) => {
        isOwner = !!(g && g.owner);
        const threadId = g ? g.id : null;
        const request$ = this.fruHttp.saveChatResponse(text, step, threadId, form_data);
        return (httpTimeoutMs && httpTimeoutMs > 0 ? request$.pipe(timeout(httpTimeoutMs)) : request$).pipe(
          catchError((err) => { this.onRequestError(isOwner); return throwError(() => err); })
        );
      }),
      map(response => {
        this.captureThreadId(response);
        if (!speculative) { this.noteAiAnswer(response); }
        return response;
      }),
      // abx 474.1: the OWNER releases the gate on ANY terminal outcome that did NOT capture a thread -- EMPTY
      // (status-0), a 2xx without success+threadid, an error, or the owner unsubscribing -- so a successor is
      // always promoted and the gate never hangs. captureThreadId already resolves it on success. A waiter
      // (isOwner false) never releases (its timeout/unsubscribe can't split the thread).
      finalize(() => { try { if (isOwner && !this.chat_threadId) { this.onRequestError(true); } } catch (e) { /* non-fatal */ } })
    );
  }

  queueAction(chatAction: ChatAction): void {
    //Queueing these actions + the concatmap above assures they are sent in order
    //With each action getting a response before the next is sent
    this.actionQueue$.next(chatAction);
  }

  saveAction(chatAction: ChatAction): Observable<any> {
    let isOwner = false;   // abx 470/474.1: a first ACTION is a valid thread initializer -- same owner-only state machine
    return this.acquireThreadId$().pipe(
      switchMap((g) => {
        isOwner = !!(g && g.owner);
        const threadId = g ? g.id : null;
        return this.fruHttp.saveAction(chatAction, threadId).pipe(
          catchError((err) => { this.onRequestError(isOwner); return throwError(() => err); })
        );
      }),
      map(response => {
        this.captureThreadId(response);
        return response;
      }),
      // abx 474.1: owner releases on any no-thread terminal outcome (EMPTY/2xx-no-threadid/error/unsubscribe)
      // so a first-action failure promotes a successor and never deadlocks later actions + Q&A.
      finalize(() => { try { if (isOwner && !this.chat_threadId) { this.onRequestError(true); } } catch (e) { /* non-fatal */ } })
    );
  }
}
